import { useEffect, useState } from "react";
import { Building2, DollarSign, CheckCircle2, Sparkles, RefreshCw, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PlatformAnalytics } from "@/components/PlatformAnalytics";
import { PlatformSecurityCard } from "@/components/platform/PlatformSecurityCard";

export default function PlatformOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/platform/stats")
      .then(({ data }) => setStats(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <main className="px-6 md:px-10 py-8" data-testid="platform-overview">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Overview</h1>
          <div className="text-xs text-slate-500">All customer companies & revenue at a glance.</div>
        </div>
        <button
          onClick={load}
          data-testid="platform-refresh-btn"
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="platform-stats">
        <StatCard Icon={Building2} label="Total companies" value={stats?.total_companies ?? "—"} />
        <StatCard Icon={Sparkles} label="On trial" value={stats?.on_trial ?? "—"} accent="amber" />
        <StatCard Icon={CheckCircle2} label="Paid · Active" value={stats?.active_paid ?? "—"} accent="emerald" />
        <StatCard Icon={AlertTriangle} label="Expired" value={stats?.expired ?? "—"} />
        <StatCard Icon={DollarSign} label="Total revenue" value={`$${(stats?.total_revenue_usd ?? 0).toFixed(2)}`} accent="primary" />
      </section>

      <PlatformSecurityCard />
      <PlatformAnalytics />
    </main>
  );
}

function StatCard({ Icon, label, value, accent }) {
  const ring = {
    primary: "ring-primary/20 bg-primary/5",
    amber: "ring-amber-200 bg-amber-50",
    emerald: "ring-emerald-200 bg-emerald-50",
  }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-xl border border-slate-200 p-5 ${ring} ring-1`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}
